import type { HelpSection, HelpTopic } from "@/interfaces/help";

/** The rail's "This screen" label when the resolver has no screen-specific label to show. */
export const HELP_RAIL_FALLBACK_LABEL = "Suggested help";

/**
 * Joins several help-screen keys in one GET /help/screen/:contextKey path segment (GFG-768), e.g.
 * `settings,wallet`. Mirrors HELP_SCREEN_KEY_SEPARATOR in apps/gfgf-api/src/constants/helpCenter.ts
 * — duplicated by design (no shared package); edit both together.
 */
export const HELP_SCREEN_KEY_SEPARATOR = ",";

/** Copy for the panel's Watch section: the rail entry and the walkthrough-video listing. */
export const HELP_WATCH = {
  HEADING: "Watch",
  WALKTHROUGH_SINGULAR: "walkthrough",
  WALKTHROUGHS: "walkthroughs",
  TOTAL_SUFFIX: "min in total",
  LIST_HEADING: "Walkthroughs",
} as const;

/** Query param carrying an article slug on the `/support` deep link that opens the panel on it. */
export const HELP_ARTICLE_QUERY_PARAM = "article";

/**
 * A helpArticle slug: Sanity's default slugify of the title (the studio's helpArticle schema sets
 * no custom one) — lowercase letters and digits in hyphen-joined runs.
 */
export const HELP_ARTICLE_SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/** What Tab can land on inside the panel — the set hooks/useFocusTrap.ts treats as tabbable. */
export const HELP_PANEL_TABBABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "textarea:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

/**
 * Help Center search limits and input debounce. MIN_QUERY_LENGTH, MAX_QUERY_LENGTH and
 * MIN_TOKEN_LENGTH mirror HELP_SEARCH in apps/gfgf-api/src/constants/helpCenter.ts — duplicated by
 * design (no shared package); edit both together or the box accepts queries the API rejects, or
 * results highlight words the API ignored.
 */
export const HELP_SEARCH = {
  MIN_QUERY_LENGTH: 2,
  MAX_QUERY_LENGTH: 100,
  MIN_TOKEN_LENGTH: 2,
  DEBOUNCE_MS: 250,
} as const;

/**
 * The Browse-by-Topic display-name contract (GFG-762): mirrors HELP_SECTIONS + HELP_SECTION_LABELS
 * in apps/gfgf-api/src/constants/helpCenter.ts and the Studio's section options list, in the same
 * order. Duplicated by design — the UI and API share no package — so edit all three together or
 * the panel rail's Topics list drifts from what the API actually serves.
 */
export const HELP_TOPICS: readonly HelpTopic[] = [
  { section: "start", label: "Getting started" },
  { section: "plans", label: "Plans & permissions" },
  { section: "credits", label: "Credits & payment" },
  { section: "collection", label: "Choosing gifts" },
  { section: "recipients", label: "Recipients & sending" },
  { section: "design", label: "Designing the gift" },
  { section: "launch", label: "Review & launch" },
  { section: "manage", label: "Managing campaigns" },
  { section: "reports", label: "Reports & impact" },
  { section: "recipient", label: "Recipient experience" },
  { section: "ref", label: "Reference & fixes" },
];

export const HELP_TOPIC_LABELS = Object.fromEntries(
  HELP_TOPICS.map((topic) => [topic.section, topic.label]),
) as Record<HelpSection, string>;
